<template>
  <div
    ref="previewImageContainer"
    @click.exact="handleClick"
    @dblclick.shift.exact="handleShiftDbClick"
  >
    <eewc-loading-spinner
      :data-testid="'preview-image-loader-' + cameraId"
      :is-loading="loading && isCameraOnline"
    />
    <div v-if="showAnalyticsPreview && previewImageContainer && isDefaultFisheyeView">
      <analytic-preview-overlay
        class="analytic-canvas"
        :analytic-settings-data="analyticSettingsData"
        :camera-id="cameraId"
        :container="previewImageContainer"
        :image-dimensions="viewDimensions"
        :show-motion-roi="usersStore.userPreferenceDataForROI?.showMotionROI"
        :show-motion-roi-and-lines="usersStore.userPreferenceDataForROI?.showAnalyticROIandLines"
      />
    </div>
    <canvas
      :id="`preview-image-${cameraId}`"
      ref="previewImage"
      class="preview-canvas"
      width="720"
      :class="{ 'preview-canvas-fill': applyObjectFill }"
      height="720"
    />
    <display-overlay
      v-if="displayOverlaySvg && showAnalytics"
      class="preview-display-overlay"
      :svg="displayOverlaySvg"
      :image-dimensions="displayOverlayDimensions"
      :container="previewImageContainer"
      :object-fill="applyObjectFill"
    />
  </div>
</template>

<script setup lang="ts">
import { authKey } from '@eencloud/eewc-components/src/service/auth.js';
import { ref, onMounted, onUnmounted, watch, computed, onBeforeUnmount, Ref } from 'vue';
import {
  useFeedsStore,
  useMediaStore,
  usePreviewImageStore,
  useUsersStore,
  useMediaShortcutStore,
  useAuthStateStore,
  useAnalyticSettingsStore,
} from '@/stores';
import { usePTZControls } from '@/service/usePTZControls';
import LivePlayer from '@een/live-video-web-sdk';
import lodash from 'lodash';
import { generateTimestamp, generateUniqueId } from '@/service/helpers';
import {
  ApiCameraWithIncludes,
  RecordedImage,
  RecordedImageParams,
  AnalyticsSettingsGet,
} from '@eencloud/eewc-components/src/service/api-types';
import VideoPlayer from '@een/live-video-web-sdk';
import DisplayOverlay from '@/components/DisplayOverlay.vue';
import AnalyticPreviewOverlay from './AnalyticPreviewOverlay.vue';
import posthog from 'posthog-js';
import { POSTHOG_EVENTS } from '@/constants/posthog';
import { captureException, addBreadcrumb, setTag } from '@/plugins/sentry';
import useFirebase from '@/service/useFirebase';

enum ERROR_TYPES {
  CANVAS_RENDERING_ERROR = 'CANVAS_RENDERING_ERROR',
  FAILED_TO_DECODE_ERROR = 'FAILED_TO_DECODE_ERROR',
  NETWORK_STREAM_REQUEST_ERROR = 'NETWORK_STREAM_REQUEST_ERROR',
  JPEG_STREAM_PARSER_ERROR = 'JPEG_STREAM_PARSER_ERROR',
}

const props = defineProps<{
  cameraId: string;
  cameraCompositeId?: string | undefined | null;
  maxHeight?: number;
  fullQualityVideo?: boolean;
  isCameraOnline?: boolean;
  isVisible?: boolean;
  paneId: string;
  camera?: ApiCameraWithIncludes;
  aspectRatio?: '16x9' | '4x3';
  ptzControlsEnabled?: Ref<boolean>;
}>();

const emit = defineEmits<{
  (e: 'timestamp', value: number | string): void;
  (e: 'videoPlayingState', value: boolean): void;
  (e: 'shiftdblclick'): void;
  (e: 'click'): void;
  (e: 'feedError'): void;
  (e: 'imageDataBuffer', value: ArrayBuffer): void;
}>();

const SUPPORTED_OVERLAY_EVENT_TYPES = [
  'een.motionDetectionEvent.v1',
  'een.objectIntrusionEvent.v1',
  'een.personDetectionEvent.v1',
  'een.vehicleDetectionEvent.v1',
  'een.gunDetectionEvent.v1',
].toString();
const previewImage = ref();
const aspectRatioCalculated = ref(false);
const addFillClass = ref(false);
const feedsStore = useFeedsStore();
const mediaStore = useMediaStore();
const previewImageStore = usePreviewImageStore();
const usersStore = useUsersStore();
const mediaShortcutStore = useMediaShortcutStore();
const authStateStore = useAuthStateStore();
const analyticSettingsStore = useAnalyticSettingsStore();

const analyticSettingsData = ref<AnalyticsSettingsGet[] | undefined>([]);
const displayOverlaySvg = ref<string | null>(null);
const eventUniqueId = ref('');
let feedApiDuration = 0;
const videoPlayEventSent = ref(false);
const isMediaShortcutSuccessful = ref(false);

const displayOverlayDimensions = ref<{ width: number; height: number } | undefined>(undefined);
const showAnalytics = computed(() => {
  return usersStore.currentUser?.previewSettings?.showAnalytics;
});
const applyObjectFill = computed(() => {
  return addFillClass.value || isFishEye.value;
});
const viewDimensions = computed(() => {
  return !isFishEye.value
    ? displayOverlayDimensions.value
    : { width: previewImageContainer.value.width, height: previewImageContainer.value.height };
});

const uniqueDeviceId = `${props.cameraId}-${props.paneId}`; // At any time, unique id must be passed jpegParser worker. The issue comes when in single view/page multiple previewImages component is rendered for same camera
const ptzControlsEnabled = computed(() => props.ptzControlsEnabled?.value);
const previewImageContainer = ref();
const showAnalyticsPreview = computed(
  () =>
    props.camera &&
    props.camera?.effectivePermissions?.editMotionAreas &&
    props.isCameraOnline &&
    props.isVisible &&
    (usersStore.userPreferenceDataForROI?.showMotionROI || usersStore.userPreferenceDataForROI?.showAnalyticROIandLines)
);

usePTZControls(previewImage, props.cameraId, ptzControlsEnabled);

const loading = ref(false);
const isPlaying = ref(false);
let player: VideoPlayer | null = null;
let reader: ReadableStreamDefaultReader | null = null;
let controller: AbortController = new AbortController();

let lastImageResponse: any = null;

const PREVIEW_DATA_CACHE_TIME = 300000;
const MAX_RECONNECT_ATTEMPTS = 20; // Maximum number of reconnection attempts
const RECONNECT_DELAY = 1000;
const reconnectAttempts = ref(0);
const isReconnecting = ref(false);
const reconnectTimer = ref<NodeJS.Timeout | null>(null);
const isUnMounted = ref(false);

onMounted(async () => {
  applyCachedPreviewData(); // apply cached image if available
  startVideoStream();
  await loadAnalyticsData();
});

watch(
  () => [props.isVisible],
  () => {
    if (props.isVisible) {
      getLastRecordedImage(); // get the latest recorded image
      props.isCameraOnline && startVideoStream();
    } else if (isPlaying.value && !props.isVisible) {
      cachePreviewData();
      stopStream();
    } else if (!props.isCameraOnline) {
      stopStream();
    }
  }
);

async function loadAnalyticsData() {
  try {
    analyticSettingsData.value = await analyticSettingsStore.getAnalyticsSettings(props.cameraId);
  } catch (error) {
    console.error('Failed to load analytics data', error);
  }
}

function retryPreviewStream() {
  if (props.isCameraOnline) {
    startVideoStream();
  }
}

function resizeEvent() {
  if (!previewImage.value) return;
  previewImage.value.width = previewImage.value.offsetWidth;
  previewImage.value.height = previewImage.value.offsetHeight;
}

watch(
  () => props.fullQualityVideo,
  (fullQuality) => {
    const resizeObserver = new ResizeObserver(lodash.debounce(resizeEvent, 200));
    isPlaying.value = false;
    if (fullQuality) {
      // stop the preview stream and remove event listener
      reader?.cancel();
      controller?.abort();
      resizeObserver.observe(previewImage.value);
    } else {
      // stop the live video player
      player?.stop();
      resizeObserver.unobserve(previewImage.value);
    }
    startVideoStream();
  }
);

watch(
  () => props.isCameraOnline,
  (online) => {
    if (online) {
      if (isPlaying.value) return;
      startVideoStream();
    } else {
      getLastRecordedImage();
      stopStream();
    }
  }
);

const handleClick = () => {
  if (!ptzControlsEnabled.value) {
    emit('click');
  }
};

const handleShiftDbClick = () => {
  if (!ptzControlsEnabled.value) {
    emit('shiftdblclick');
  }
};

async function getLastRecordedImage() {
  const params: RecordedImageParams = {
    deviceId: props.cameraId,
    timestamp__lte: generateTimestamp(Date.now()),
    type: 'preview',
  };
  const res = await mediaStore.getRecordedImage(params, false, controller);

  res?.data && renderOnCanvas(res);
}

function startVideoStream() {
  if (props.isVisible && !isPlaying.value) {
    window.addEventListener('online', () => scheduleReconnect());
    eventUniqueId.value = generateUniqueId(props.cameraId);
    setTag('een_event_id', eventUniqueId.value);
    logAnalyticsEvent(POSTHOG_EVENTS.USER_REQUESTS_LIVE_PREVIEW_VIDEO);
    isPlaying.value = true;
    if (props.fullQualityVideo) {
      startFullQualityStream();
    } else {
      tryMediaShortcut();
      preparePreviewStream();
    }
  }
}
const isFishEye = computed(() => props.camera?.capabilities?.ptz?.fisheye);
const isDefaultFisheyeView = computed(() =>
  props.cameraCompositeId === undefined ? true : props.cameraCompositeId === 'default-fisheye'
);

async function renderOnCanvas(res: RecordedImage) {
  try {
    const previewImageCanvas = previewImage.value;
    if (!previewImageCanvas || !res.data) return;

    lastImageResponse = res;
    if (isFishEye.value) {
      emit('imageDataBuffer', res.data);
    } else {
      const bitmap = await createImageBitmap(new Blob([res.data], { type: 'image/jpeg' }));
      const ctx = previewImageCanvas.getContext('2d');
      // add fill class if aspect ratio is 16:9 otherwise normal class is there by default
      if (!aspectRatioCalculated.value) {
        const shouldAddFillClass = previewImageCanvas.width < 1.2 * bitmap.width;
        aspectRatioCalculated.value = true;
        addFillClass.value = shouldAddFillClass;
      }

      previewImageCanvas.height = bitmap.height;
      previewImageCanvas.width = bitmap.width;
      displayOverlayDimensions.value = { width: bitmap.width, height: bitmap.height };
      ctx.drawImage(bitmap, 0, 0, previewImageCanvas.width, previewImageCanvas.height);
      bitmap.close();
    }
    res.timestamp && emit('timestamp', res.timestamp);
    displayOverlaySvg.value = res.overlaySvg ?? null;
  } catch (error) {
    handleError(ERROR_TYPES.CANVAS_RENDERING_ERROR, `Error rendering image on canvas: ${error}`);
  }
}

/**
 * Attempts to set up a media shortcut for the given camera.
 * If the shortcut is successfully set up, it stops the current preview stream because the media shortcut should take over immediately.
 */
async function tryMediaShortcut() {
  const shortcutSuccessful = await mediaShortcutStore.tryPreviewStreamShortcut(
    props.cameraId,
    renderOnCanvas,
    preparePreviewStream
  );
  if (shortcutSuccessful) {
    isMediaShortcutSuccessful.value = true;
    isPlaying.value = true;
    emit('videoPlayingState', true);
    controller.abort();
  } else {
    isMediaShortcutSuccessful.value = false;
  }
}

function handlePreviewVideoPlayEvent() {
  // Track video play event with PostHog analytics
  if (!videoPlayEventSent.value) {
    logAnalyticsEvent(POSTHOG_EVENTS.USER_VIEWS_LIVE_PREVIEW_VIDEO, {
      een_media_shortcut: isMediaShortcutSuccessful.value,
      een_feed_api_duration: feedApiDuration,
    });
    videoPlayEventSent.value = true; // Prevent sending the event again
  }
}

async function preparePreviewStream() {
  const signal = controller.signal;
  const canvas = previewImage.value;
  const feedsApiStartTime = Date.now();
  const feed = await feedsStore.getPreviewFeed(props.cameraId, signal, eventUniqueId.value);
  feedApiDuration = Date.now() - feedsApiStartTime;
  if (!feed) {
    emit('feedError');
    return;
  }

  if (canvas) {
    loading.value = true;
    if (window.jpegStreamParser && feed) {
      const overlayIdParam = showAnalytics.value ? `&overlayId__in=${SUPPORTED_OVERLAY_EVENT_TYPES}` : '';
      startPreviewStream(`${feed.multipartUrl}&isoTimestamps=true${overlayIdParam}`);
      window.jpegStreamParser.addEventListener(
        'message',
        async ({ data }: { data: { cameraId: string; value: RecordedImage } | { error: Error } }) => {
          try {
            if (data.error) {
              handleError(ERROR_TYPES.JPEG_STREAM_PARSER_ERROR, data.error);
            } else if (data.cameraId === uniqueDeviceId) {
              isPlaying.value = true;
              emit('videoPlayingState', true);
              await renderOnCanvas(data.value);
              handlePreviewVideoPlayEvent();
            }
          } catch (error) {
            handleError(ERROR_TYPES.FAILED_TO_DECODE_ERROR, `Failed to decode: ${error}`);
            emit('videoPlayingState', false);
            isPlaying.value = false;
          }
        },
        {
          capture: false,
          once: false,
          passive: false,
          signal: signal,
        }
      );
    } else {
      loading.value = false;
    }
  }
}

async function startFullQualityStream() {
  if (isFishEye.value) return false;
  loading.value = true;
  const feed = await feedsStore.getMainFeed(props.cameraId);
  previewImage.value.width = previewImage.value.offsetWidth;
  previewImage.value.height = previewImage.value.offsetHeight;

  player = new LivePlayer();
  player.start({
    canvasElement: previewImage.value,
    onFrame: (time) => onFrame(time),
    onStop: () => {
      emit('videoPlayingState', false);
      isPlaying.value = false;
    },
    feedUrl: feed?.multipartUrl,
    jwt: authKey(),
  });

  function onFrame(time: number) {
    loading.value = false;
    isPlaying.value = true;
    emit('videoPlayingState', true);
    emit('timestamp', time);
    // Reset reconnect state on successful frame reception
    resetReconnectState(true);
  }
}

async function startPreviewStream(streamUrl: string | URL | Request) {
  try {
    loading.value = true;
    const res = await fetch(streamUrl, {
      signal: controller.signal,
      headers: {
        Authorization: 'bearer ' + authKey(),
        'x-ee-request-id': eventUniqueId.value,
      },
    });
    if (!res.ok) {
      // Log non-2xx HTTP responses
      handleError(ERROR_TYPES.NETWORK_STREAM_REQUEST_ERROR, `Stream response error: ${res.status} - ${res.statusText}`);
    }

    // forcefully reroute to login multipart api returns 401
    if (res.status === 401) {
      logout();
    }

    if (res.body) {
      reader = res.body.getReader();
      let result = await reader.read();
      while (!result.done) {
        const transfer = [result.value.buffer];
        window.jpegStreamParser?.postMessage({ cameraId: uniqueDeviceId, value: result.value }, transfer);
        loading.value = false;
        result = await reader.read();
      }

      if (result.done) {
        console.debug('Preview Stream is done for camera:', props.cameraId);
        isPlaying.value = false;
        loading.value = false;
        scheduleReconnect();
      }
    }
  } catch (err: any) {
    isPlaying.value = false;
    loading.value = false;
    console.log(err);
    handleError(ERROR_TYPES.NETWORK_STREAM_REQUEST_ERROR, `Stream request error: ${err}`);
    scheduleReconnect();
  }
}

function resetJpegStreamBuffer() {
  // clean up the partial data from the buffer
  window.jpegStreamParser?.postMessage({ cameraId: uniqueDeviceId });
}

function applyCachedPreviewData() {
  const cachedData = previewImageStore.images[props.cameraId] as unknown as RecordedImage;
  if (
    cachedData &&
    cachedData.timestamp &&
    new Date(cachedData.timestamp) > new Date(Date.now() - PREVIEW_DATA_CACHE_TIME)
  ) {
    setTimeout(() => renderOnCanvas(cachedData), isFishEye.value ? 500 : 0); // 500ms delay in rendering is done for fisheye sdk to be loaded
  }
}

function cachePreviewData() {
  if (lastImageResponse) {
    previewImageStore.images[props.cameraId] = lastImageResponse;
  }
}

function stopStream() {
  window.removeEventListener('online', () => scheduleReconnect());
  resetReconnectState();
  if (props.fullQualityVideo) {
    player?.stop();
  } else {
    mediaShortcutStore.dropCamera(props.cameraId);
    reader?.cancel();
    controller?.abort();
    resetJpegStreamBuffer();
    emit('videoPlayingState', false);
    isPlaying.value = false;
    controller = new AbortController();
  }
}

/**
 * Logs an analytics event with the given event name and additional data to PostHog.
 * The event includes camera ID, timestamp, unique event ID, and any additional data provided.
 * Only logs if the current user's clusterId is in the allowed clusters from Firebase Remote Config.
 */
async function logAnalyticsEvent(eventName: string, eventData: Record<string, string | number | boolean> = {}) {
  try {
    const { getStringFlag } = useFirebase();
    const allowedClustersString = await getStringFlag('allowed_clusters_posthog_preview_video', '');

    if (!allowedClustersString) {
      return; // Do not log if no allowed clusters are configured
    }

    const allowedClusters = allowedClustersString.split(',').map((cluster: string) => cluster.trim());
    const currentClusterId = usersStore.userContext?.clusterId;
    if (!currentClusterId || !allowedClusters.includes(currentClusterId)) {
      return; // Do not log if not in the allowed clusters
    }
    posthog.capture(eventName, {
      een_camera_id: props.cameraId,
      een_timestamp: Date.now(),
      een_event_id: eventUniqueId.value,
      een_camera_status: props.camera?.status?.connectionStatus || '',
      een_camera_make: props.camera?.deviceInfo?.make || '',
      een_camera_model: props.camera?.deviceInfo?.model || '',
      een_bridge_id: props.camera?.bridgeId || '',
      een_analog_input: props.camera?.analog?.input || '',
      ...eventData,
    });
  } catch (error) {
    addBreadcrumb({
      category: 'firebase.remote.config',
      message: 'Error reading Firebase Remote Config for analytics',
      level: 'error',
    });
    captureException(error);
    console.error('Error reading Firebase Remote Config for analytics:', error);
    return;
  }
}

function handleError(type: ERROR_TYPES, error: Error | string | Event) {
  const errorObject = createErrorObject(error);
  const deviceInfo = {
    cameraId: props.cameraId,
    cameraStatus: props.camera?.status?.connectionStatus || '',
    mediaShortcut: !!isMediaShortcutSuccessful.value,
    timestamp: Date.now(),
    eventId: eventUniqueId.value,
  };
  logError(errorObject, deviceInfo);
  console.error(`[${type}]`, errorObject, deviceInfo);
}

function logError(errorObject: Error, deviceInfo: Record<string, string | number | boolean>) {
  // Add contextual breadcrumb before capturing the exception
  addBreadcrumb({
    category: 'preview.live.video.error',
    message: `Preview live video error in preview image component: ${errorObject.message}`,
    level: 'error',
    data: deviceInfo,
  });

  captureException(errorObject);
  logAnalyticsEvent(POSTHOG_EVENTS.USER_ENCOUNTERS_VIDEO_ERROR, {
    een_error_message: errorObject.message,
    een_error_stack: errorObject.stack || '',
    een_media_shortcut: !!isMediaShortcutSuccessful.value || '',
    een_video_type: 'live-preview',
  });
}

function logout() {
  authStateStore.logout();
}

function createErrorObject(error: Error | string | Event): Error {
  // If it's already an Error instance, just ensure stack exists
  if (error instanceof Error) {
    if (!error.stack) error.stack = '';
    return error;
  }

  // For string errors, create new Error
  if (typeof error === 'string') {
    const errorObj = new Error(error);
    errorObj.stack = '';
    return errorObj;
  }

  // For Event errors
  if (error instanceof Event) {
    const errorObj = new Error(error.type);
    errorObj.stack = '';
    return errorObj;
  }

  // Fallback for unknown error types
  const errorObj = new Error(String(error));
  errorObj.stack = '';
  return errorObj;
}

onBeforeUnmount(() => {
  isUnMounted.value = true;
  setTag('een_event_id', undefined);
  cachePreviewData();
});

onUnmounted(() => {
  stopStream();
  analyticSettingsStore.analyticSettings = [];
});

function scheduleReconnect() {
  if (isMediaShortcutSuccessful.value) return;
  stopStream();
  if (isUnMounted.value) {
    return;
  }
  if (isReconnecting.value || reconnectAttempts.value >= MAX_RECONNECT_ATTEMPTS) {
    emit('feedError');
    return;
  }
  isReconnecting.value = true;
  reconnectAttempts.value++;

  const delay = reconnectAttempts.value < 10 ? RECONNECT_DELAY : 10 * RECONNECT_DELAY; // Increase delay after 10 attempts to 10 seconds else 1 second
  reconnectTimer.value = setTimeout(() => {
    if (props.isVisible && props.isCameraOnline) {
      console.log(`Attempting to reconnect preview stream #${reconnectAttempts.value} for camera: ${props.cameraId}`);
      startVideoStream();
    }
    isReconnecting.value = false;
  }, delay);
}

function resetReconnectState(resetCounter: boolean = false) {
  if (resetCounter) {
    reconnectAttempts.value = 0;
  }
  isReconnecting.value = false;
  if (reconnectTimer.value) {
    clearTimeout(reconnectTimer.value);
    reconnectTimer.value = null;
  }
}
</script>

<style lang="scss" scoped>
@import '../styles/public/main.scss';

.eewc-loading-spinner {
  z-index: 30;
}

.analytics-canvas {
  z-index: 20;
}

.preview-canvas {
  width: 100%;
  height: 100%;
  background: $primary;
  object-fit: contain;
  cursor: pointer;
  position: absolute;
  top: 0;
  left: 0;

  &-fill {
    object-fit: fill;
  }
}
</style>
